/**
 * Sentry Configuration
 * Centralized error and performance monitoring
 * AUTO-DETECTS environment and adapts configuration
 * 
 * Installation required:
 * npm install @sentry/vue @sentry/tracing
 * 
 * Configuration required in .env (SINGLE config for ALL envs):
 * VUE_APP_SENTRY_DSN=your-sentry-dsn
 * 
 * Environment auto-detection:
 * - localhost, 127.0.0.1 → development
 * - Contains "staging", "staging." → staging
 * - Everything else → production
 */

import { logger } from "@/utils/logger";
import { App } from 'vue';

interface SentryConfig {
  environment: 'development' | 'staging' | 'production';
  tracesSampleRate: number;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  enableLogging: boolean;
}

/**
 * Auto-detect environment based on hostname and NODE_ENV
 */
function detectEnvironment(): 'development' | 'staging' | 'production' {
  const nodeEnv = process.env.NODE_ENV;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Check if development (local)
  if (nodeEnv === 'development' || hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }

  // Check if staging
  if (hostname.includes('staging') || hostname.startsWith('staging.')) {
    return 'staging';
  }

  // Default to production
  return 'production';
}

/**
 * Get Sentry config based on detected environment
 */
function getSentryConfig(): SentryConfig {
  const environment = detectEnvironment();

  const configs: Record<string, SentryConfig> = {
    development: {
      environment: 'development',
      tracesSampleRate: 0.5, // Capture 50% for debugging
      logLevel: 'debug',
      enableLogging: true,
    },
    staging: {
      environment: 'staging',
      tracesSampleRate: 1.0, // Capture 100% for QA
      logLevel: 'info',
      enableLogging: true,
    },
    production: {
      environment: 'production',
      tracesSampleRate: 0.1, // Capture 10% for cost optimization
      logLevel: 'warn',
      enableLogging: false,
    },
  };

  return configs[environment];
}

export function initSentry(app: App): void {
  // Only initialize Sentry if DSN is configured
  if (!process.env.VUE_APP_SENTRY_DSN) {
    logger.warn('[Sentry] DSN not configured - error tracking disabled');
    return;
  }

  // Auto-detect environment and get config
  const sentryConfig = getSentryConfig();

  logger.info('[Sentry] Auto-detected environment', {
    environment: sentryConfig.environment,
    tracesSampleRate: sentryConfig.tracesSampleRate,
  });

  // Dynamic import to avoid bundling Sentry if not needed
  try {
    const Sentry = require('@sentry/vue');
    const { CaptureConsole } = require('@sentry/integrations');

    Sentry.init({
      app,
      dsn: process.env.VUE_APP_SENTRY_DSN,
      environment: sentryConfig.environment,
      tracesSampleRate: sentryConfig.tracesSampleRate,
      
      // Integrations
      integrations: [
        new CaptureConsole({
          levels: sentryConfig.environment === 'production' ? ['error'] : ['warn', 'error'],
        }),
      ],

      // Before Send - filter sensitive data
      beforeSend(event: any) {
        // Remove sensitive headers
        if (event.request) {
          delete event.request.headers?.Authorization;
          delete event.request.headers?.['X-Auth-Token'];
        }

        // Remove sensitive cookies
        if (event.request?.cookies) {
          delete event.request.cookies;
        }

        // In production: limit breadcrumbs for cost optimization
        if (sentryConfig.environment === 'production' && event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.slice(-5); // Keep only last 5
        }

        return event;
      },

      // Ignore certain errors
      ignoreErrors: [
        // Browser extension errors
        'top.GLOBALS',
        // Random plugins/extensions errors
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        // Network timeouts
        'NetworkError',
        // User cancelled
        'AbortError',
      ],

      // Allow errors from common domains
      allowUrls: [
        /https?:\/\/.*yourdomain\.com/,
        /https?:\/\/.*staging\.yourdomain\.com/,
        /localhost/,
        /127\.0\.0\.1/,
      ],
    });

    logger.info(`[Sentry] Initialized (${sentryConfig.environment})`);
  } catch (error) {
    logger.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Capture a message in Sentry
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  try {
    const Sentry = require('@sentry/vue');
    Sentry.captureMessage(message, level);
  } catch (error) {
    logger.error('Failed to capture message in Sentry:', error);
  }
}

/**
 * Capture an exception in Sentry
 */
export function captureException(error: Error | string, context?: Record<string, any>): void {
  try {
    const Sentry = require('@sentry/vue');
    Sentry.captureException(error, {
      contexts: {
        application: context,
      },
    });
  } catch (sentryError) {
    logger.error('Failed to capture exception in Sentry:', sentryError);
  }
}

/**
 * Add custom context to Sentry
 */
export function setSentryUser(userId: string, email?: string, username?: string): void {
  try {
    const Sentry = require('@sentry/vue');
    Sentry.setUser({
      id: userId,
      email,
      username,
    });
  } catch (error) {
    logger.error('Failed to set Sentry user:', error);
  }
}

/**
 * Clear Sentry user
 */
export function clearSentryUser(): void {
  try {
    const Sentry = require('@sentry/vue');
    Sentry.setUser(null);
  } catch (error) {
    logger.error('Failed to clear Sentry user:', error);
  }
}
